import {
    isSidepanelHost,
    kSidepanelHostParam,
    kSidepanelHostValue,
} from "@/client/utils/getAppEnvironment";

/**
 * Keeps the `?spliceHost=sidepanel` marker on the URL across in-app
 * navigation, so the Creative Partner panel's Labs iframe stays in website
 * mode after the first page.
 *
 * The panel appends the marker to the iframe URL it loads. In-app navigation
 * is a full page load that drops the query string, and the marker was carried
 * only by sessionStorage — which a cross-origin iframe can have partitioned or
 * blocked. When that happens the read fails silently, `getAppEnvironment()`
 * falls through to `AppEnvironment.Plugin`, and the app sits waiting for
 * resource messages the panel never answers: a dropzone stuck on
 * "Loading: <name>" with nothing behind it.
 *
 * The URL is the only thing guaranteed to survive a same-origin page load in a
 * partitioned iframe, so the marker rides it. Doing that at every link would
 * mean remembering at every link, which is the same failure this is fixing, so
 * it is done once for the document.
 */

/** Adds the marker to a same-origin href. Returns others untouched. */
export const withSidepanelHostParam = (href: string, base: string): string => {
    try {
        const url = new URL(href, base);
        if (url.origin !== new URL(base).origin) {
            return href;
        }
        url.searchParams.set(kSidepanelHostParam, kSidepanelHostValue);
        return url.toString();
    } catch {
        return href;
    }
};

/**
 * Whether a click on this anchor is an in-app page load worth marking.
 *
 * Deliberately narrow. Result rows download through an anchor carrying a
 * `blob:` href and a `download` attribute, and rewriting one of those would
 * break an export to fix a navigation bug, so both are excluded — by protocol
 * and by attribute, independently.
 */
export const isInternalNavigationLink = (anchor: HTMLAnchorElement, base: string): boolean => {
    if (anchor.hasAttribute("download")) {
        return false;
    }
    const target = anchor.getAttribute("target");
    if (target !== null && target !== "" && target !== "_self") {
        return false;
    }
    try {
        const url = new URL(anchor.href, base);
        if (url.protocol !== "http:" && url.protocol !== "https:") {
            return false;
        }
        return url.origin === new URL(base).origin;
    } catch {
        return false;
    }
};

/**
 * Call once per page, unconditionally, from every boot path — the home
 * directory (`client/index.ts`) and apps (`createCore`), the same two places
 * the web auth gate is wired into, because the home page has no Core.
 *
 * **Do not put this call inside a branch on the host.** It decides for itself
 * whether this is a sidepanel host and no-ops everywhere else, so a caller
 * that guards it can only put it somewhere that guard is already false. It
 * shipped that way once: installed in the non-sidepanel branch of `createCore`,
 * where it could never run. The bug that hid behind it is nasty, because
 * home → app still worked (the home page installs it) and only the trip back
 * lost the marker.
 */
export const installSidepanelHostMarker = (): void => {
    if (typeof document === "undefined" || !isSidepanelHost()) {
        return;
    }
    // Capture phase, so the href carries the marker before any handler reads
    // it and before the browser begins the navigation.
    document.addEventListener(
        "click",
        (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const anchor = target.closest("a");
            if (anchor === null || !isInternalNavigationLink(anchor, window.location.href)) {
                return;
            }
            anchor.href = withSidepanelHostParam(anchor.href, window.location.href);
        },
        true,
    );
};
